import './review-sections.css'

interface Priority {
  area: string
  rationale: string
  bsc_perspective: string
  urgency: string
}

interface SuggestedInitiative {
  name: string
  description: string
  expected_impact: string
  bsc_perspective: string
}

interface BlueprintData {
  priorities: Priority[]
  suggested_initiatives: SuggestedInitiative[]
  risk_watch: string[]
  carry_forward_from_this_cycle: string[]
}

interface Props {
  data: BlueprintData
  onStartNewCycle?: () => void
  startingCycle?: boolean
}

function getBscClass(perspective: string): string {
  const p = perspective.toLowerCase()
  if (p.includes('financial')) return 'bsc-financial'
  if (p.includes('customer')) return 'bsc-customer'
  if (p.includes('process')) return 'bsc-process'
  if (p.includes('learning') || p.includes('growth')) return 'bsc-learning'
  return ''
}

export default function BlueprintSection({ data, onStartNewCycle, startingCycle }: Props) {
  return (
    <div>
      <p className="section-hint">
        Recommended priorities and initiatives for your next strategy cycle, based on everything above.
      </p>

      {/* Priorities */}
      {(data.priorities || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Priority Areas</h4>
          {data.priorities.map((p, i) => (
            <div key={i} className="review-card">
              <div className="review-card-header">
                <span className="review-card-title">{p.area}</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={`bsc-badge ${getBscClass(p.bsc_perspective)}`}>
                    {p.bsc_perspective}
                  </span>
                  <span className={`urgency-badge urgency-${p.urgency}`}>
                    {p.urgency}
                  </span>
                </div>
              </div>
              <p className="review-card-body">{p.rationale}</p>
            </div>
          ))}
        </div>
      )}

      {/* Suggested Initiatives */}
      {(data.suggested_initiatives || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Suggested Initiatives</h4>
          {data.suggested_initiatives.map((init, i) => (
            <div key={i} className="review-card">
              <div className="review-card-header">
                <span className="review-card-title">{init.name}</span>
                <span className={`bsc-badge ${getBscClass(init.bsc_perspective)}`}>
                  {init.bsc_perspective}
                </span>
              </div>
              <p className="review-card-body">{init.description}</p>
              <p style={{ fontSize: 12, color: '#2e7d32', marginTop: 4 }}>
                Expected impact: {init.expected_impact}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Risk Watch */}
      {(data.risk_watch || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Risk Watch</h4>
          <ul className="risk-list">
            {data.risk_watch.map((risk, i) => (
              <li key={i}>{risk}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Carry Forward */}
      {(data.carry_forward_from_this_cycle || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Carry Forward</h4>
          <ul className="carry-forward-list">
            {data.carry_forward_from_this_cycle.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Start New Cycle button */}
      {onStartNewCycle && (
        <button
          className="btn-start-cycle"
          disabled={startingCycle}
          onClick={onStartNewCycle}
        >
          {'\u{1F331}'} {startingCycle ? 'Starting...' : 'Start New Cycle'}
        </button>
      )}
    </div>
  )
}
